import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { cva } from 'class-variance-authority';
import cn from 'classnames';

import {
  mapSpecContract,
  specContractRequirenments,
} from '@/common/config/contract';
import {
  type PaidSpecContractType,
  type SpecContractType,
} from '@/common/types/contract';
import { ModalContext } from '@/components/Modals/Modal/config';
import { TextSmall } from '@/components/Typography/Typography';
import { AvailableInfo } from '@/components/ui/AvailableInfo/AvailableInfo';
import { MainButton } from '@/components/ui/Button/Button';
import { changeTutorial } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { ContractBenefits } from '../ContractBenefits/ContractBenefits';

import { AboutImage } from './AboutImage';
import { description } from './config';

import css from './styles.module.scss';

const aboutSpecContractVariant = cva('', {
  variants: {
    req: {
      Grand: css.grandReq,
      GrandPlus: css.grandReq,
      Custom: css.customReq,
      Golden: css.goldenReq,
      GoldenPlus: css.goldenReq,
      Blitz: css.blitzReq,
      BlitzPlus: css.blitzReq,
    },
    description: {
      Grand: css.grandDescr,
      GrandPlus: css.grandDescr,
      Custom: css.customDescr,
      Golden: css.goldenDescr,
      GoldenPlus: css.goldenDescr,
      Blitz: css.blitzDescr,
      BlitzPlus: css.blitzDescr,
    },
    benefits: {
      Grand: css.grandBenefits,
      GrandPlus: css.grandBenefits,
      Custom: css.customBenefits,
      Golden: css.goldenBenefits,
      GoldenPlus: css.goldenBenefits,
      Blitz: css.blitzBenefits,
      BlitzPlus: css.blitzBenefits,
    },
    button: {
      Grand: css.grandButton,
      GrandPlus: css.grandButton,
      Custom: css.customButton,
      Golden: css.goldenButton,
      GoldenPlus: css.goldenButton,
      Blitz: css.blitzButton,
      BlitzPlus: css.blitzButton,
    },
  },
});

interface AboutSpecContractProps {
  kind: SpecContractType | PaidSpecContractType;
  onClose: () => void;
}

const AboutSpecContractUi: FC<AboutSpecContractProps> = ({ kind, onClose }) => {
  const { t } = useTranslation();
  const level = specContractRequirenments[kind].level;

  return (
    <div className="w-full flex flex-col items-center pb-4 px-4">
      <AboutImage kind={kind} />
      <div className="w-full flex flex-col items-center relative z-[2]">
        <AvailableInfo
          className={aboutSpecContractVariant({ req: kind })}
          level={level}
        />
        <TextSmall
          className={cn(
            '!text-white/60 !leading-1.2 text-center',
            aboutSpecContractVariant({ description: kind }),
          )}
        >
          {t(description[kind])}
        </TextSmall>
        <ContractBenefits
          className={aboutSpecContractVariant({ benefits: kind })}
          kind={kind}
        />
        <MainButton
          onClick={onClose}
          text={t('Got it!')}
          className={cn(aboutSpecContractVariant({ button: kind }))}
        />
      </div>
    </div>
  );
};

const withClose = (
  Component: FC<AboutSpecContractProps>,
): FC<Omit<AboutSpecContractProps, 'onClose'>> => {
  return (props: Omit<AboutSpecContractProps, 'onClose'>) => {
    const { closeModal } = useContext(ModalContext);
    const dispatch = useAppDispatch();
    const userId = useAppSelector((state) => state.user.user?.id);

    return (
      <Component
        {...props}
        onClose={() => {
          if (userId) {
            dispatch(
              changeTutorial({
                kind: mapSpecContract[props.kind],
                userId,
              }),
            );
          }

          closeModal();
        }}
      />
    );
  };
};

const AboutSpecContract = withClose(AboutSpecContractUi);

export { AboutSpecContract, AboutSpecContractUi };
